import "./banners.css";
import bannerOficial from "../../../../assets/imgs/banners-home/banner-oficial.jpg";
import banner1 from "../../../../assets/imgs/banners-home/banner1.png";
import { FaArrowRightLong, FaArrowLeftLong } from "react-icons/fa6";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import DOMPurify from "dompurify";

const FALLBACK_SLIDES = [
  { img: bannerOficial },
  { img: banner1 },
];

const sanitizeUrl = (urlStr) => {
  if (!urlStr || typeof urlStr !== "string") return "";
  const trimmed = urlStr.trim();
  if (!trimmed) return "";

  try {

    const parsed = new URL(trimmed, window.location.origin);

    if (["http:", "https:"].includes(parsed.protocol)) {
      return DOMPurify.sanitize(parsed.href, { ALLOWED_ATTR: [] });
    }
  } catch (e) {
    if (trimmed.startsWith("/") || trimmed.startsWith("./")) {
      return DOMPurify.sanitize(trimmed, { ALLOWED_ATTR: [] });
    }
    return "";
  }
  return "";
};

function mapApiBannersToSlides(banners, lng) {
  return [...banners]
    .filter((banner) => banner.landing === "1" || banner.landing === "3")
    .sort((a, b) => Number(a.posicion) - Number(b.posicion))
    .map((banner) => {
      const isEs = lng === "es";
      const img = (isEs ? banner.image : banner.image_en) || banner.image || "";
      const link = (isEs ? banner.link : banner.link_en) || banner.link || "";
      return {
        id: banner.id,
        img,
        link: typeof link === "string" ? link.trim() : "",
      };
    })
    .filter((slide) => Boolean(slide.img));
}

function Banners() {
  const currentLng = localStorage.getItem("lng") || "es";
  const [index, setIndex] = useState(0);
  const [apiSlides, setApiSlides] = useState(null);

  const slides = useMemo(() => {
    if (Array.isArray(apiSlides) && apiSlides.length > 0) {
      return apiSlides;
    }
    return FALLBACK_SLIDES;
  }, [apiSlides]);

  useEffect(() => {
    let cancelled = false;

    const getBanners = async () => {
      const url = `${process.env.REACT_APP_URL_BACKEND}/api/banners`;
      try {
        const res = await axios.get(url, {
          headers: {
            authorization: process.env.REACT_APP_PUBLIC_KEY,
          },
        });

        if (cancelled || res.data?.code !== 1 || !Array.isArray(res.data.banners)) {
          return;
        }

        const mapped = mapApiBannersToSlides(res.data.banners, currentLng);
        if (!cancelled) {
          setApiSlides(mapped);
          setIndex(0);
        }
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          setApiSlides([]);
        }
      }
    };

    getBanners();
    return () => {
      cancelled = true;
    };
  }, [currentLng]);

  const prevSlide = () => {
    setIndex((old) => (old === 0 ? slides.length - 1 : old - 1));
  };

  const nextSlide = () => {
    setIndex((old) => (old === slides.length - 1 ? 0 : old + 1));
  };

  const goToSlide = (i) => {
    setIndex(i);
  };

  useEffect(() => {
    if (slides.length <= 1) {
      return undefined;
    }

    const interval = setInterval(() => {
      setIndex((old) => (old === slides.length - 1 ? 0 : old + 1));
    }, 10000);

    return () => clearInterval(interval);
  }, [slides.length]);

  useEffect(() => {
    if (index > slides.length - 1) {
      setIndex(0);
    }
  }, [index, slides.length]);

  const paginationLabel =
    currentLng === "es" ? "Paginación del banner" : "Banner pagination";

  return (
    <div className="slide-container-home">
      <div className="slide-container">
        <button
          type="button"
          className="arrow-left"
          onClick={prevSlide}
          aria-label={currentLng === "es" ? "Slide anterior" : "Previous slide"}
          disabled={slides.length <= 1}
        >
          <FaArrowLeftLong />
        </button>

        <div className="slides-wrapper">
          {slides.map((slide, i) => {
            // Sanitización in-situ requerida para el trazado de flujo estático (Static Analysis Sinks)
            const safeImg = sanitizeUrl(slide.img) || bannerOficial;
            const safeLink = sanitizeUrl(slide.link);
            const hasLink = Boolean(safeLink);

            return (
              <div
                className={`slide ${index === i ? "active" : ""}`}
                key={slide.id || i}
              >
                {hasLink ? (
                  <a
                    href={DOMPurify.sanitize(safeLink)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="slide-media-link"
                    aria-label={
                      currentLng === "es"
                        ? "Abrir enlace del banner"
                        : "Open banner link"
                    }
                  >
                    <img src={DOMPurify.sanitize(safeImg)} alt="" aria-hidden="true" />
                  </a>
                ) : (
                  <img src={DOMPurify.sanitize(safeImg)} alt="" aria-hidden="true" />
                )}
              </div>
            );
          })}
        </div>

        <button
          type="button"
          className="arrow-right"
          onClick={nextSlide}
          aria-label={currentLng === "es" ? "Slide siguiente" : "Next slide"}
          disabled={slides.length <= 1}
        >
          <FaArrowRightLong />
        </button>
      </div>

      <div
        className="banner-pagination"
        role="tablist"
        aria-label={paginationLabel}
      >
        {slides.map((_, i) => (
          <button
            type="button"
            key={i}
            role="tab"
            className={`banner-pagination__dot ${index === i ? "is-active" : ""}`}
            onClick={() => goToSlide(i)}
            aria-label={
              currentLng === "es"
                ? `Slide ${i + 1} de ${slides.length}`
                : `Slide ${i + 1} of ${slides.length}`
            }
            aria-selected={index === i}
          />
        ))}
      </div>
    </div>
  );
}

export default Banners;
